import { schoolActions } from './school-slice';
import store from './index';
import { handleError, handleSessionExpired } from './error-handler';

export const fetchSchoolYearsList = () => {
    return async (dispatch) => {
        const fetchData = async () => {
            dispatch(schoolActions.setIsLoading(true));
            const bearer = 'Bearer ' + localStorage.getItem('token');
            const url = store.getState().auth.url;
            const response = await fetch(url + 'api/schoolyears', {
                headers: {
                    'Authorization': bearer,
                }
            });

            if (!response.ok) {
                if (response.status === 401) {
                    handleSessionExpired(dispatch, bearer);
                    throw new Error('Your session has expired. Please log in again.');
                } else {
                    throw new Error('Could not fetch school years list.');
                }
            }

            const data = await response.json();
            return data;
        };

        try {
            const yearsData = await fetchData();
            dispatch(schoolActions.loadSchoolYears({ schoolYears: yearsData || [] }));
        } catch (error) {
            handleError(dispatch, error);
        } finally {
            dispatch(schoolActions.setIsLoading(false));
        }
    };
};

export const fetchSchoolTypesList = () => {
    return async (dispatch) => {
        const fetchData = async () => {
            dispatch(schoolActions.setIsLoading(true));
            const bearer = 'Bearer ' + localStorage.getItem('token');
            const url = store.getState().auth.url;
            const response = await fetch(url + 'api/schooltypes', {
                headers: {
                    'Authorization': bearer,
                }
            });

            if (!response.ok) {
                if (response.status === 401) {
                    handleSessionExpired(dispatch, bearer);
                    throw new Error('Your session has expired. Please log in again.');
                } else {
                    throw new Error('Could not fetch school types list.');
                }
            }

            const data = await response.json();
            return data;
        };

        try {
            const typesData = await fetchData();
            dispatch(schoolActions.loadSchoolTypes({ schoolTypes: typesData || [] }));
        } catch (error) {
            handleError(dispatch, error);
        } finally {
            dispatch(schoolActions.setIsLoading(false));
        }
    };
};

const authHeaders = () => ({
    Authorization: 'Bearer ' + localStorage.getItem('token'),
});

const readSaveError = async (response) => {
    try {
        const body = await response.json();
        if (body && body.Message) return body.Message;
    } catch (e) {
        // The body was not JSON. The caller shows the fallback.
    }
    return 'Could not save the year and school settings.';
};

// Header and parent financials both need this. Share one request so the second caller does not fetch again.
let gridContextRequest = null

export const fetchSchoolGridContext = () => {
    return (dispatch) => {
        if (store.getState().school.gridSettingsLoaded) return Promise.resolve(true)
        if (gridContextRequest) return gridContextRequest

        const load = async () => {
            const bearer = 'Bearer ' + localStorage.getItem('token');
            const url = store.getState().auth.url;
            const revisionAtStart = store.getState().school.gridRevision;
            try {
                const [settingsResponse, schoolsResponse] = await Promise.all([
                    fetch(url + 'api/schoolGridSettings', { headers: authHeaders() }),
                    fetch(url + 'api/schoolGridSchools', { headers: authHeaders() }),
                ]);

                if (settingsResponse.status === 401 || schoolsResponse.status === 401) {
                    handleSessionExpired(dispatch, bearer);
                    return false;
                }

                if (!settingsResponse.ok || !schoolsResponse.ok) {
                    throw new Error('Could not load year and school settings.');
                }

                const settings = await settingsResponse.json();
                const schools = await schoolsResponse.json();
                const latest = store.getState().school;
                const savedDuringLoad = latest.gridRevision !== revisionAtStart;
                dispatch(schoolActions.loadGridContext({
                    year: savedDuringLoad ? latest.gridYear : (settings.Year || ''),
                    schools: savedDuringLoad ? latest.gridSchools : (settings.Schools || ''),
                    schoolsList: schools || [],
                }));
                return true;
            } catch (error) {
                handleError(dispatch, error);
                return false;
            }
        }

        gridContextRequest = load().finally(() => {
            gridContextRequest = null
        })
        return gridContextRequest
    };
};

export const saveSchoolGridSetting = (parameter, value) => {
    return async (dispatch) => {
        const bearer = 'Bearer ' + localStorage.getItem('token');
        const url = store.getState().auth.url;
        const response = await fetch(url + 'api/schoolGridSettings', {
            method: 'POST',
            headers: {
                ...authHeaders(),
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({ Parameter: parameter, Value: value == null ? '' : String(value) }),
        });

        if (!response.ok) {
            if (response.status === 401) {
                handleSessionExpired(dispatch, bearer);
                throw new Error('Your session has expired. Please log in again.');
            }
            throw new Error(await readSaveError(response));
        }

        if (parameter === 'YearGrids') {
            dispatch(schoolActions.setGridYear(value == null ? '' : String(value)));
        } else if (parameter === 'SchoolsGrids') {
            dispatch(schoolActions.setGridSchools(value == null ? '' : String(value)));
        }
        dispatch(schoolActions.bumpGridRevision());
    };
};

export const beginSchoolListFetch = () => store.getState().school.gridRevision

export const commitSchoolListFetch = (dispatch, list, revisionAtStart) => {
    if (store.getState().school.gridRevision !== revisionAtStart) return false
    dispatch(schoolActions.noteListLoaded({ list, revision: revisionAtStart }))
    return true
}


